import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSetlist } from '~/modules/songs/hooks/use-setlist';
import SongDao from '~/modules/songs/songs-service';

export default function useSongIndex(includeDeleted = false) {
  'use no memo'; // React Compiler: SongDao.getCurrentIndex() reads mutable external state directly (not via useState/useSyncExternalStore), so the compiler caches it as non-reactive and `isLoading` gets stuck at its initial value.
  const currentIndex = SongDao.getCurrentIndex();
  const [songIndex, setSongIndex] = useState(() => currentIndex || []);
  const [loadFailed, setLoadFailed] = useState(false);
  const setlist = useSetlist();

  const loadSongs = useCallback(
    () =>
      SongDao.getIndex(includeDeleted)
        .then((songs) => {
          setLoadFailed(false);
          setSongIndex(songs);
        })
        .catch(async (error: Error) => {
          // Swallowed until now, and the rejection left `currentIndex` null for good - so the song
          // screen sat on loading skeletons with nothing anywhere to say why. The desktop app is
          // what makes it reachable: it serves the index itself, so a local server that never
          // started is indistinguishable from a library still loading.
          setLoadFailed(true);
          console.error('Could not load the song index', error);
          // No auto-close: the index is loaded once per visit, so this does not clear itself, and
          // a five second toast on a screen across the room is a message nobody was looking at.
          (await import('react-toastify')).toast.error(`Could not load the song list: ${error.message}`, {
            autoClose: false,
            // One id, because several screens hold this hook at once and all of them fail together -
            // the room does not need to be told three times.
            toastId: 'song-index-load-failed',
          });
        }),
    [includeDeleted],
  );

  useEffect(() => {
    // setTimeout(loadSongs, 3000);
    loadSongs();
  }, [loadSongs]);

  const finalSongIndex = useMemo(
    () => (setlist.songList ? songIndex.filter((song) => setlist.songList?.includes(song.shortId)) : songIndex),
    [setlist.songList, songIndex],
  );

  return {
    data: finalSongIndex,
    reload: loadSongs,
    isLoading: currentIndex === null && !loadFailed,
  };
}
